import type { Emulator } from "../emulator.js";

export const TRACKER_PLAYER_ENTRY = 0x4600;
export const TRACKER_STATUS_ADDRESS = 0x4f00;
export const TRACKER_STATUS_SIZE = 32;
export const TRACKER_BYTECODE_ADDRESS = 0x4f20;
export const TRACKER_WORKSPACE_END = 0x5fff;
export const TRACKER_BYTECODE_MAX_SIZE = TRACKER_WORKSPACE_END - TRACKER_BYTECODE_ADDRESS + 1;

export const TRACKER_STATUS_STATE = {
  empty: 0,
  written: 1,
  playing: 2,
  stopped: 3,
  error: 4
} as const;

export type TrackerStatusState = (typeof TRACKER_STATUS_STATE)[keyof typeof TRACKER_STATUS_STATE];

export type TrackerEmulatorStatus = {
  audioDiagnostics: string;
  audioStatus: string;
  bytecodeSize: number;
  currentOrder: number;
  currentPattern: number;
  currentRow: number;
  currentTick: number;
  currentTempo: number;
  currentSpeed: number;
  errorCode: number;
  loop: boolean;
  muteMask: number;
  ownedByTracker: boolean;
  soloMask: number;
  startOrder: number;
  startRow: number;
  state: TrackerStatusState;
  stopRequested: boolean;
  tickReload: number;
  valid: boolean;
  workspaceWritten: boolean;
};

function readWord(bytes: Uint8Array, offset: number): number {
  return (bytes[offset] ?? 0) | ((bytes[offset + 1] ?? 0) << 8);
}

export function createTrackerStatusBlock(config: {
  bytecodeSize: number;
  loop: boolean;
  muteMask: number;
  soloMask: number;
  startOrder: number;
  startRow: number;
  tickReload: number;
}): Uint8Array {
  const bytes = new Uint8Array(TRACKER_STATUS_SIZE);
  bytes[0] = 0x54;
  bytes[1] = 0x52;
  bytes[2] = 0x4b;
  bytes[3] = 0x31;
  bytes[4] = TRACKER_STATUS_STATE.written;
  bytes[5] = config.startOrder & 0xff;
  bytes[6] = config.startRow & 0xff;
  bytes[7] = config.loop ? 1 : 0;
  bytes[8] = config.muteMask & 0x0f;
  bytes[9] = config.soloMask & 0x0f;
  bytes[10] = 0;
  bytes[11] = config.startOrder & 0xff;
  bytes[12] = config.startRow & 0xff;
  bytes[13] = 0;
  bytes[14] = 0;
  bytes[15] = 0;
  bytes[16] = 0;
  bytes[17] = config.bytecodeSize & 0xff;
  bytes[18] = (config.bytecodeSize >> 8) & 0xff;
  bytes[19] = config.tickReload & 0xff;
  bytes[20] = (config.tickReload >> 8) & 0xff;
  bytes[21] = 0;
  return bytes;
}

export function readTrackerEmulatorStatus(emulator: Emulator, audioStatus: string, audioDiagnostics: string): TrackerEmulatorStatus {
  const bytes = emulator.copyMemory(TRACKER_STATUS_ADDRESS, TRACKER_STATUS_SIZE);
  const valid = bytes[0] === 0x54 && bytes[1] === 0x52 && bytes[2] === 0x4b && bytes[3] === 0x31;
  if (!valid) {
    return {
      audioDiagnostics,
      audioStatus,
      bytecodeSize: 0,
      currentOrder: 0,
      currentPattern: 0,
      currentRow: 0,
      currentTick: 0,
      currentTempo: 0,
      currentSpeed: 0,
      errorCode: 0,
      loop: false,
      muteMask: 0,
      ownedByTracker: false,
      soloMask: 0,
      startOrder: 0,
      startRow: 0,
      state: TRACKER_STATUS_STATE.empty,
      stopRequested: false,
      tickReload: 0,
      valid: false,
      workspaceWritten: false
    };
  }
  const state = (bytes[4] ?? 0) as TrackerStatusState;
  return {
    audioDiagnostics,
    audioStatus,
    bytecodeSize: readWord(bytes, 17),
    currentOrder: bytes[11] ?? 0,
    currentPattern: bytes[14] ?? 0,
    currentRow: bytes[12] ?? 0,
    currentTick: bytes[13] ?? 0,
    currentTempo: bytes[16] ?? 0,
    currentSpeed: bytes[15] ?? 0,
    errorCode: bytes[21] ?? 0,
    loop: (bytes[7] ?? 0) !== 0,
    muteMask: bytes[8] ?? 0,
    ownedByTracker: state === TRACKER_STATUS_STATE.playing || state === TRACKER_STATUS_STATE.written,
    soloMask: bytes[9] ?? 0,
    startOrder: bytes[5] ?? 0,
    startRow: bytes[6] ?? 0,
    state,
    stopRequested: (bytes[10] ?? 0) !== 0,
    tickReload: readWord(bytes, 19),
    valid: true,
    workspaceWritten: state !== TRACKER_STATUS_STATE.empty
  };
}
